import { useEffect, useState } from 'react';
import { History } from 'lucide-react';
import { api } from '../../lib/api';
import { VorgaengeTab } from './VorgaengeTab';
import { EventTimelineTab } from './EventTimelineTab';
import { AktionenPanel } from './AktionenPanel';
import { DocumentGrid } from './DocumentGrid';
import { DAUER_SOLL, DAUER_CATEGORIES } from './documentSets';

/** Aufgeklappter Kunden-Bereich: links (flexibel) die Vorgänge mit ihren Dokumenten —
 *  dort liegt die Arbeit. Rechts (feste Breite) der ruhige Teil: die einmal
 *  hochgeladenen Dauer-Dokumente als Statuszeile, darunter Aktionen bzw. Verlauf.
 *  Inline in der Kundenliste und auf der Detailseite wiederverwendet. */
export function CustomerDetailPanel({ customerId }: { customerId: number }) {
  const [customer, setCustomer] = useState<any>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [dauerStand, setDauerStand] = useState<{ vorhanden: number; gesamt: number; fehlend: string[]; fehlendAktuell: string[] } | null>(null);
  const [reiter, setReiter] = useState<'aktionen' | 'verlauf'>('aktionen');

  // Liegen alle Dauer-Dokumente unterschrieben und aus dem laufenden Kalenderjahr vor,
  // gibt es auf Kundenebene nichts mehr zu tun: der Aktionen-Reiter entfällt und mit ihm
  // die Umschaltung — dann steht dort nur noch der Verlauf. Zum Jahreswechsel werden die
  // Unterschriften ungültig, die Aktionen (und damit die Reiter) kommen von selbst zurück.
  // Solange der Stand unbekannt ist (dauerStand === null), wird der Block gar nicht
  // gerendert — sonst blitzt beim Aufklappen kurz die falsche Fassung auf.
  const aktionenNoetig = !!dauerStand && dauerStand.fehlendAktuell.length > 0;

  useEffect(() => {
    let alive = true;
    api.get<any[]>('/api/customers')
      .then(list => { if (alive) setCustomer(list.find(c => c.id === customerId) ?? null); })
      .catch(() => { if (alive) setCustomer(null); });
    return () => { alive = false; };
  }, [customerId]);

  const reiterKnopf = (aktiv: boolean) =>
    `flex-1 px-3 py-2 text-xs font-medium rounded-md transition-colors ${
      aktiv
        ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
        : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
    }`;

  return (
    <div className="flex flex-col min-[1070px]:flex-row gap-x-6 gap-y-6 animate-in fade-in slide-in-from-top-1 duration-200">
      {/* Links: Vorgänge — die Hauptspalte, nimmt den restlichen Platz. */}
      <div className="min-w-0 flex-1">
        <VorgaengeTab customerId={customerId} customerEmail={customer?.email ?? null} />
      </div>

      {/* Rechts: fester Streifen für Dauer-Dokumente, Aktionen und Verlauf. */}
      <div className="min-w-0 min-[1070px]:w-[380px] min-[1070px]:shrink-0 min-[1070px]:border-l border-slate-200 dark:border-slate-700 min-[1070px]:pl-6 space-y-4">
        <DocumentGrid
          title="Dauer-Dokumente"
          level={{ customerId }}
          soll={DAUER_SOLL}
          categories={DAUER_CATEGORIES}
          signedOnly
          refreshKey={refreshKey}
          collapse="immer"
          onStatus={setDauerStand}
          layout="liste"
          customerEmail={customer?.email}
          uploadNurWennUnvollstaendig
        />

        <div className={dauerStand ? '' : 'invisible'}>
          {aktionenNoetig ? (
            <div className="flex gap-1 p-1 mb-3 rounded-lg bg-slate-200/70 dark:bg-slate-800">
              <button type="button" onClick={() => setReiter('aktionen')} className={reiterKnopf(reiter === 'aktionen')}>Aktionen</button>
              <button type="button" onClick={() => setReiter('verlauf')} className={reiterKnopf(reiter === 'verlauf')}>Verlauf</button>
            </div>
          ) : (
            /* Gleiche Einrückung, Icon- und Schriftgröße wie die Statuszeile der
               Dauer-Dokumente darüber — beide Blöcke beginnen auf einer Linie. */
            <div className="flex items-center gap-2 px-2.5 py-2.5 mb-1">
              <History className="w-4 h-4 text-slate-500 dark:text-slate-400 shrink-0" />
              <h4 className="text-sm font-bold text-slate-900 dark:text-white">Verlauf</h4>
            </div>
          )}
          {aktionenNoetig && reiter === 'aktionen' ? (
            <AktionenPanel
              customerId={customerId}
              customerEmail={customer?.email ?? null}
              dauerVollstaendig={!!dauerStand && dauerStand.fehlend.length === 0}
              onChanged={() => setRefreshKey(k => k + 1)}
            />
          ) : (
            <EventTimelineTab customerId={customerId} />
          )}
        </div>
      </div>
    </div>
  );
}
